import styled, { css } from "styled-components";

export const c = {
    slate50: "#f8fafc",
    slate100: "#f1f5f9",
    slate200: "#e2e8f0",
    slate300: "#cbd5e1",
    slate400: "#94a3b8",
    slate500: "#64748b",
    slate600: "#475569",
    slate700: "#334155",
    slate800: "#1e293b",
    slate900: "#0f172a",
    amber50: "#5dc904",
    amber100: "#6fb327",
    amber300: "#6fb327",
    amber400: "#3db264",
    amber500: "#9ecd7e",
    amber700: "#010407",
    amber900: "#f8f7f6",
    red50: "#fef2f2",
    red100: "#fee2e2",
    red300: "#fca5a5",
    red500: "#ef4444",
    red600: "#dc2626",
    white: "#ffffff",
};

export const media = {
    sm: "@media (min-width: 640px)",
    md: "@media (min-width: 768px)",
    lg: "@media (min-width: 1024px)",
};

/* Layout */
export const Container = styled.div`
    margin: 0 auto;
    padding: 24px 16px 64px;
    ${media.sm} {
        padding-left: 24px;
        padding-right: 24px;
    }
`;
export const Header = styled.header`
    margin-bottom: 24px;
`;
export const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;
export const StatsRow = styled.div`
    display: none;
    margin-left: auto;
    align-items: center;
    gap: 24px;
    ${media.sm} {
        display: flex;
    }
`;
export const StatBox = styled.div`
    text-align: right;
`;
export const StatValue = styled.div`
    font-size: 18px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    line-height: 1;
    color: ${c.slate900};
`;
export const StatLabel = styled.div`
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${c.slate400};
`;

export const TabsNav = styled.nav`
    margin-top: 20px;
    display: flex;
    gap: 4px;
    border-bottom: 1px solid ${c.slate200};
`;
export const Tab = styled.button<{ active?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: none;
    cursor: pointer;
    margin-bottom: -1px;
    padding: 10px 16px;
    font-size: 14px;
    font-weight: 500;
    border-bottom: 2px solid ${(p) => (p.active ? c.amber400 : "transparent")};
    color: ${(p) => (p.active ? c.slate900 : c.slate500)};
    transition: color 0.15s;
    &:hover {
        color: ${(p) => (p.active ? c.slate900 : c.slate800)};
    }
`;

export const Card = styled.div`
    border: 1px solid ${c.slate200};
    border-radius: 12px;
    background: ${c.white};
    padding: 16px;
`;

export const FieldLabel = styled.label`
    display: block;
    margin-bottom: 6px;
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${c.slate500};
`;

const fieldBase = css<{ error?: boolean }>`
    width: 100%;
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 14px;
    color: ${c.slate900};
    background: ${c.white};
    border: 1px solid ${(p) => (p.error ? c.red300 : c.slate200)};
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
    &::placeholder {
        color: ${c.slate400};
    }
    &:focus {
        border-color: ${(p) => (p.error ? c.red300 : c.amber400)};
        box-shadow: 0 0 0 2px ${(p) => (p.error ? c.red100 : c.amber100)};
    }
`;
export const Input = styled.input<{ error?: boolean; mono?: boolean }>`
    ${fieldBase}
    font-variant-numeric: ${(p) => (p.mono ? "tabular-nums" : "normal")};
`;
export const Select = styled.select<{ error?: boolean }>`
    ${fieldBase}
    &:disabled {
        background: ${c.slate50};
        color: ${c.slate400};
        cursor: not-allowed;
    }
`;

export const PrimaryButton = styled.button<{ sm?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    background: ${c.amber400};
    color: ${c.slate900};
    font-size: 14px;
    font-weight: 500;
    padding: ${(p) => (p.sm ? "6px 12px" : "8px 16px")};
    transition: background 0.15s;
    &:hover {
        background: ${c.amber500};
    }
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;
export const GhostButton = styled.button<{ sm?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: 1px solid ${c.slate200};
    border-radius: 8px;
    cursor: pointer;
    background: ${c.white};
    color: ${c.slate700};
    font-size: 14px;
    font-weight: 500;
    padding: ${(p) => (p.sm ? "6px 10px" : "8px 12px")};
    transition: background 0.15s;
    &:hover {
        background: ${c.slate50};
    }
`;

/* Inventory layout */
export const Split = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    ${media.lg} {
        flex-direction: row;
    }
`;
export const Sidebar = styled.aside`
    width: 100%;
    flex-shrink: 0;
    ${media.lg} {
        width: 288px;
    }
`;
export const ResultsCol = styled.section`
    min-width: 0;
    flex: 1;
`;
export const NavList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;
export const NavButton = styled.button<{ active?: boolean }>`
    display: flex;
    width: 100%;
    align-items: center;
    gap: 8px;
    border: none;
    cursor: pointer;
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 14px;
    font-weight: 500;
    text-align: left;
    transition: background 0.15s;
    background: ${(p) => (p.active ? c.amber100 : "transparent")};
    color: ${(p) => (p.active ? c.amber900 : c.slate600)};
    &:hover {
        background: ${(p) => (p.active ? c.amber100 : c.slate50)};
    }
`;
export const DeptGroupNav = styled.div`
    margin-top: 8px;
`;
export const DeptHeaderNav = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${c.slate400};
`;
export const CountSpan = styled.span`
    margin-left: auto;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    color: ${c.slate400};
`;

export const FiltersHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
`;
export const FiltersTitle = styled.p`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${c.slate500};
    margin: 0;
`;
export const ClearButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    color: ${c.amber700};
    &:hover {
        text-decoration: underline;
    }
`;
export const HelpText = styled.p`
    font-size: 14px;
    line-height: 1.6;
    color: ${c.slate500};
    margin: 0;
`;
export const FilterGroups = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;
export const FilterGroupLabel = styled.p`
    margin: 0 0 8px;
    font-size: 12px;
    font-weight: 500;
    color: ${c.slate700};
`;
export const ChipRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;
export const Chip = styled.button<{ on?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    border-radius: 9999px;
    padding: 4px 10px;
    font-size: 12px;
    font-weight: 500;
    transition: border-color 0.15s, background 0.15s;
    border: 1px solid ${(p) => (p.on ? c.amber300 : c.slate200)};
    background: ${(p) => (p.on ? c.amber100 : c.white)};
    color: ${(p) => (p.on ? c.amber900 : c.slate600)};
    &:hover {
        border-color: ${(p) => (p.on ? c.amber300 : c.slate300)};
    }
`;

export const ResultsHeader = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
`;
export const ResultCount = styled.p`
    font-size: 14px;
    color: ${c.slate600};
    margin: 0;
`;
export const MonoStrong = styled.span`
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${c.slate900};
`;
export const Muted = styled.span`
    color: ${c.slate400};
`;

export const EmptyBox = styled.div`
    border: 1px dashed ${c.slate200};
    border-radius: 12px;
    background: ${c.white};
    padding: 56px 24px;
    text-align: center;
`;
export const EmptyIcon = styled.div`
    margin: 0 auto 12px;
    display: flex;
    height: 44px;
    width: 44px;
    align-items: center;
    justify-content: center;
    border-radius: 9999px;
    background: ${c.slate100};
`;
export const EmptyTitle = styled.p`
    font-weight: 500;
    color: ${c.slate800};
    margin: 0;
`;
export const EmptyBody = styled.p`
    margin: 4px auto 0;
    max-width: 24rem;
    font-size: 14px;
    color: ${c.slate500};
`;

/* Add form */
export const FormWrap = styled.div`
    max-width: 42rem;
    margin: 0 auto;
`;
export const FormCard = styled(Card)`
    padding: 24px;
`;
export const FormTitle = styled.h2`
    font-size: 16px;
    font-weight: 600;
    margin: 0;
`;
export const Grid = styled.div<{ cols?: number }>`
    display: grid;
    gap: 16px;
    grid-template-columns: 1fr;
    ${media.sm} {
        grid-template-columns: repeat(${(p) => p.cols || 2}, 1fr);
    }
`;
export const Col = styled.div<{ span?: number }>`
    ${media.sm} {
        grid-column: span ${(p) => p.span || 1};
    }
`;
export const RequiredMark = styled.span`
    color: ${c.red500};
`;
export const ErrorText = styled.p`
    margin: 4px 0 0;
    font-size: 12px;
    color: ${c.red600};
`;
export const Block = styled.div<{ mt?: number }>`
    margin-top: ${(p) => p.mt || 0}px;
`;
export const SectionMini = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
`;
export const SectionMiniLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${c.slate400};
`;
export const HLine = styled.span`
    height: 1px;
    flex: 1;
    background: ${c.slate100};
`;
export const DashedBox = styled.div`
    margin-top: 24px;
    border: 1px dashed ${c.slate200};
    border-radius: 8px;
    background: ${c.slate50};
    padding: 32px 16px;
    text-align: center;
`;
export const DashedText = styled.p`
    font-size: 14px;
    color: ${c.slate500};
    margin: 0;
`;
export const FooterRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    border-top: 1px solid ${c.slate100};
    padding-top: 20px;
    margin-top: 28px;
`;
export const CheckboxRow = styled.label`
    display: flex;
    cursor: pointer;
    align-items: center;
    gap: 12px;
    border: 1px solid ${c.slate200};
    border-radius: 8px;
    background: ${c.white};
    padding: 10px 12px;
`;
export const Checkbox = styled.input`
    height: 16px;
    width: 16px;
    accent-color: ${c.amber500};
`;
export const CheckboxText = styled.span`
    font-size: 14px;
    color: ${c.slate700};
`;

/* Modal scaffold bits (assign modal) */
export const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

export const FlexContainer = styled.div`
    display: flex;
    justify-content: space-between;
`;

export const FooterContentContainer = styled.div`
    width: 100%;
`;
export const FooterButtonsContainer = styled.div<{ update?: boolean }>`
    width: 100%;
    display: flex;
    justify-content: ${(p) => (p.update ? "space-between" : "flex-end")};
`;
export const AssignContext = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    border: 1px solid ${c.slate200};
    border-radius: 8px;
    background: ${c.slate50};
    font-weight: 500;
    color: ${c.slate800};
`;
export const AssignAvail = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: ${c.slate500};
    font-variant-numeric: tabular-nums;
`;

/* Serial search */
export const SearchWrap = styled.div`
    position: relative;
    margin-left: auto;
    width: 260px;
`;
export const SearchResults = styled.div`
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 20;
    max-height: 320px;
    overflow-y: auto;
    border: 1px solid ${c.slate200};
    border-radius: 8px;
    background: ${c.white};
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
`;
export const SearchResultRow = styled.button`
    display: flex;
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    border: none;
    border-bottom: 1px solid ${c.slate100};
    background: none;
    cursor: pointer;
    padding: 8px 12px;
    text-align: left;
    &:last-child {
        border-bottom: none;
    }
    &:hover {
        background: ${c.slate50};
    }
`;
export const SearchResultSerial = styled.span`
    font-size: 13px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${c.slate900};
`;
export const SearchResultMeta = styled.span`
    font-size: 12px;
    color: ${c.slate500};
`;
export const SearchEmpty = styled.p`
    margin: 0;
    padding: 10px 12px;
    font-size: 13px;
    color: ${c.slate500};
`;

/* Status badge */
const statusColor: Record<string, string> = {
    available: c.amber700,
    issued: c.slate700,
    in_repair: "#b45309",
    damaged: c.red600,
    discarded: c.slate400,
};
export const StatusBadge = styled.span<{ status: string }>`
    display: inline-flex;
    align-items: center;
    border-radius: 4px;
    padding: 2px 8px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: ${c.slate100};
    color: ${(p) => statusColor[p.status] || c.slate700};
`;

/* UnitDetailModal */
export const ButtonRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
`;
export const InlineForm = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 12px;
    padding: 12px;
    border-radius: 8px;
    background: ${c.slate50};
`;
export const AttrList = styled.dl`
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 4px 12px;
    margin: 0;
`;
export const AttrKey = styled.dt`
    font-size: 13px;
    color: ${c.slate500};
`;
export const AttrValue = styled.dd`
    margin: 0;
    font-size: 13px;
    color: ${c.slate800};
    font-weight: 500;
`;
export const HistoryList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 220px;
    overflow-y: auto;
`;
export const HistoryItem = styled.div`
    border-left: 2px solid ${c.slate200};
    padding: 2px 0 2px 10px;
`;
export const HistoryMeta = styled.div`
    font-size: 12px;
    color: ${c.slate400};
`;
export const HistoryText = styled.div`
    font-size: 13px;
    color: ${c.slate800};
`;
